export const PIP_SKINS = [
  { value: 'default', labelKey: 'settings.pipSkinDefault', category: 'daily', width: 380, height: 300 },
  { value: 'game-hud', labelKey: 'settings.pipSkinHud', category: 'daily', width: 380, height: 300 },
  { value: 'stream-classic', labelKey: 'stream.classic', category: 'stream', width: 480, height: 128 },
  { value: 'stream-minimal', labelKey: 'stream.minimal', category: 'stream', width: 480, height: 96 },
  { value: 'stream-mushroom', labelKey: 'stream.mushroom', category: 'stream', width: 320, height: 208 },
  // Preserve stored selections while replacing these two designs.
  { value: 'stream-camp', labelKey: 'stream.camp', category: 'stream', width: 340, height: 240 },
  { value: 'stream-clockwork', labelKey: 'stream.clockwork', category: 'stream', width: 340, height: 240 },
  { value: 'stream-pinkbean', labelKey: 'stream.pinkbean', category: 'stream', width: 340, height: 240 },
  { value: 'stream-kerning', labelKey: 'stream.kerning', category: 'stream', width: 420, height: 184 },
  { value: 'stream-aqua', labelKey: 'stream.aqua', category: 'stream', width: 360, height: 240 },
] as const

export type PipSkin = typeof PIP_SKINS[number]['value']
export type StreamSkin = Extract<PipSkin, `stream-${string}`>
export type AdventureSkin = Extract<StreamSkin, 'stream-camp' | 'stream-clockwork' | 'stream-pinkbean'>
export type SceneSkin = Extract<StreamSkin, 'stream-kerning' | 'stream-aqua'>
export function isSceneSkin(value: StreamSkin): value is SceneSkin {
  return value === 'stream-kerning' || value === 'stream-aqua'
}
export function isAdventureSkin(value: StreamSkin): value is AdventureSkin {
  return value === 'stream-camp' || value === 'stream-clockwork' || value === 'stream-pinkbean'
}
export const STREAM_BACKGROUNDS = { white: '#ffffff', magenta: '#ff00ff', green: '#00ff00', blue: '#0000ff', solid: '#15171c' } as const
export type StreamBackground = keyof typeof STREAM_BACKGROUNDS | `#${string}` | 'transparent'
export interface PipStreamOptions { background: StreamBackground; dailyBackground?: StreamBackground }

export function resolvePipBackground(value: StreamBackground): string {
  return Object.hasOwn(STREAM_BACKGROUNDS, value) ? STREAM_BACKGROUNDS[value as keyof typeof STREAM_BACKGROUNDS] : value
}

export function isStreamSkin(value: PipSkin): value is StreamSkin {
  return PIP_SKINS.some((skin) => skin.value === value && skin.category === 'stream')
}

export function normalizePipStreamOptions(value: unknown): PipStreamOptions {
  const input = value && typeof value === 'object' ? value as Partial<PipStreamOptions> : {}
  const background = normalizeBackground(input.background) ?? 'transparent'
  const dailyBackground = normalizeBackground(input.dailyBackground)
  return dailyBackground ? { background, dailyBackground } : { background }
}

function normalizeBackground(value: unknown): StreamBackground | undefined {
  const color = typeof value === 'string' ? value.trim().toLowerCase() : ''
  if (color === 'transparent' || Object.hasOwn(STREAM_BACKGROUNDS, color)) return color as StreamBackground
  if (/^#(?:[\da-f]{3,4}|[\da-f]{6}|[\da-f]{8})$/.test(color)) {
    return (color.length <= 5 ? `#${[...color.slice(1)].map((digit) => digit + digit).join('')}` : color) as StreamBackground
  }
  return undefined
}

export function defaultPipColors(dark: boolean) {
  return dark
    ? { bg: '#111317', surface: '#1a1d24', subtle: '#252a34', text: '#f8fafc', muted: '#94a3b8', border: '#2e3542', danger: '#f87171', dangerSoft: '#3b1c1c', green: '#34d399', greenSoft: '#132e23', exp: '#fbbf24', mesos: '#fb923c', amber: '#fbbf24', amberSoft: '#382d16', hp: '#fb7185', mp: '#38bdf8' }
    : { bg: '#f5f7fa', surface: '#ffffff', subtle: '#edf2f7', text: '#0f172a', muted: '#64748b', border: '#e2e8f0', danger: '#dc2626', dangerSoft: '#fef2f2', green: '#059669', greenSoft: '#ecfdf5', exp: '#b45309', mesos: '#c2410c', amber: '#d97706', amberSoft: '#fffbeb', hp: '#e11d48', mp: '#0284c7' }
}

export function normalizePipSkin(value: unknown): PipSkin {
  return PIP_SKINS.find((skin) => skin.value === value)?.value ?? 'default'
}
